import { useState } from 'react';
import { Button, Card, Modal } from '@playerbase/ui';
import type { Playtest } from '@playerbase/types';

import { disablePlaytest } from '../../../../games/playtests-data.js';

interface DisableConfirmModalProps {
  open: boolean;
  playtest: Playtest;
  onClose: () => void;
  onDisabled: (playtest: Playtest) => void;
}

const displayNameOf = (playtest: Playtest): string =>
  playtest.name.trim() === '' ? 'Untitled playtest' : playtest.name;

export function DisableConfirmModal({
  open,
  playtest,
  onClose,
  onDisabled,
}: DisableConfirmModalProps): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm(): Promise<void> {
    setBusy(true);
    setError(null);
    const result = await disablePlaytest(playtest.id);
    if (!result.ok || result.data === null) {
      setBusy(false);
      setError('Could not disable the playtest. Please try again.');
      return;
    }
    setBusy(false);
    onDisabled(result.data);
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!busy) {
          setError(null);
          onClose();
        }
      }}
      labelledById={`disable-playtest-${playtest.id}`}
      busy={busy}
      size="sm"
    >
      <Card>
        <Card.Header>
          <Card.Title id={`disable-playtest-${playtest.id}`}>
            Disable playtest
          </Card.Title>
          <Card.Description>
            Take <strong>{displayNameOf(playtest)}</strong> offline? Paired
            testers will stop receiving DMs when its events fire. Existing
            sessions and responses are kept, and you can re-activate later.
          </Card.Description>
        </Card.Header>
        {error !== null ? (
          <Card.Content>
            <p className="text-sm text-danger-text">{error}</p>
          </Card.Content>
        ) : null}
        <Card.Footer>
          <Button
            variant="ghost"
            onClick={() => {
              setError(null);
              onClose();
            }}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => void handleConfirm()}
            disabled={busy}
          >
            {busy ? 'Disabling...' : 'Disable playtest'}
          </Button>
        </Card.Footer>
      </Card>
    </Modal>
  );
}
